import { Organization } from "../../../organization/dto/Organization";
import {
  FormBuilder,
  FormControl,
  FormGroup,
  Validators,
} from "@angular/forms";
import { ModalButtons, SpinnerProps } from "../../../shared/component/modal-container/modal-container.component";
import { DataRoomEmployeeNotificationDTO } from "./data-room-org-users-notification.util";
import { BoxEntityType } from "../../service/box-file-structure.service";
import {
  BoxItemAction,
  CLEAR_ALL_ACTION,
  CREATE_FOLDER_ACTION,
  DOWNLOAD_ACTION,
  SELECT_ALL_ACTION,
  UPLOAD_ACTION,
} from "../box.util";

export const shareLink = '/documents/data-room/external' as string;

export const logCountPerPage = 20;

export enum DATA_ROOM_URLS {
  ADMIN = 'documents/data-room',
}

export enum DataRoomUserLogAction {
  LOGIN = 'LOGIN',
  VIEW = 'VIEW',
  DOWNLOAD = 'DOWNLOAD',
  ARCHIVE_DOWNLOAD = 'ARCHIVE_DOWNLOAD',
  UPLOAD = 'UPLOAD',
}

export enum DRExternalUserState {
  INVITE = 'INVITE',
  LOGIN = 'LOGIN',
}

export enum DataRoomFolderUserRole {
  VIEW = 'VIEW',
  DOWNLOAD = 'DOWNLOAD',
  UPLOAD = 'UPLOAD',
  NO_ACCESS = 'NO_ACCESS',
}

export const UPLOAD_ACTIONS = [UPLOAD_ACTION, CREATE_FOLDER_ACTION];
export const SELECT_ACTIONS = [SELECT_ALL_ACTION, CLEAR_ALL_ACTION];

export const actionTextMap: { [key in DataRoomUserLogAction] : string } = {
  [DataRoomUserLogAction.LOGIN]: 'Logged in',
  [DataRoomUserLogAction.VIEW]: 'Viewed document',
  [DataRoomUserLogAction.DOWNLOAD]: 'Downloaded document',
  [DataRoomUserLogAction.ARCHIVE_DOWNLOAD]: 'Downloaded document',
  [DataRoomUserLogAction.UPLOAD]: 'Uploaded',
};

export const actionExtUserLogMap: { [key in DRExternalUserState] : string } = {
  [DRExternalUserState.INVITE]: 'Invited',
  [DRExternalUserState.LOGIN]: 'Logged in',
};

const rolesEntries = [
  [DataRoomFolderUserRole.VIEW, 'View'],
  [DataRoomFolderUserRole.DOWNLOAD, 'View & Download'],
  [DataRoomFolderUserRole.UPLOAD, 'View & Download & Upload'],
  [DataRoomFolderUserRole.NO_ACCESS, 'No access'],
] as const;

export const rolesMapWithoutNoAccess: Map<DataRoomFolderUserRole, string> = new Map(
  rolesEntries.filter(([role]) => role !== DataRoomFolderUserRole.NO_ACCESS)
);

export const rolesMapWithoutUpload: Map<DataRoomFolderUserRole, string> = new Map(
  rolesEntries.filter(([role]) => role !== DataRoomFolderUserRole.UPLOAD)
);

export interface RefreshBoxToken {
  refreshToken: string;
  role: DataRoomFolderUserRole
}

export interface DataRoomUserLogDocumentInfo {
  documentName: string;
  documentId: string;
  parentFolderId: string;
  parentFolderName: string;
  boxEntityType: BoxEntityType
}

export interface LogData {
  action: DataRoomUserLogAction;
  documents: DataRoomUserLogDocumentInfo[];
  rootFolderId: string;
}

export class OrgAndUserInfo {
  orgName: string;
  orgLogo: string;
  email: string;
  fullName: string;
  adminLogin: boolean;
  role: DataRoomFolderUserRole;
}

export class DataRoomTokens {
  boxAccessToken: string;
  userToken: string;
}

export class DRExternalUserAccessFolder {
  dataRoomFolderId: string;
  dataRoomFolderName: string;
}

export class DRExternalUserAccessesInOrg {
  orgUUID: string;
  orgName: string;
  userOrgFolders: DRExternalUserAccessFolder[];
}

export class DataRoomExternalUserAccesses {
  email: string;
  orgs: DRExternalUserAccessesInOrg[];
}

export class DRExternalUserRq {
  email: string;
  dataRoomFolderId: string;
  orgUUID: string;
}

export class DataRoomAdminWrapperDTO {
  dataRoomAdminFoldersDTOList: DataRoomOrgFolderDTO[];
  dataRoomExternalLogList: DataRoomUserLogDTO[];
  totalActivityLogAmount: number;

  boxAccessToken: string;

  constructor() {
    this.dataRoomAdminFoldersDTOList = [];
    this.dataRoomExternalLogList = [];
    this.boxAccessToken = null;
  }
}

/**
 * Data transfer object representing a folder within an organization's data room.
 */
export interface DataRoomOrgFolderDTO {
  folderId: string;
  /**
   * List of funds to which the folder has been published.
   * */
  publishedToFunds: DataRoomFundWithRoleRs[];
  folderName: string;
  isClicked: boolean;
  dataRoomExternalLogList: DataRoomUserLogDTO[];
  totalActivityLogAmount: number;
  externalUserMemberships: ExternalUserFolderMembershipDTO[];
  notifications: DataRoomEmployeeNotificationDTO[];
}

export interface DataRoomFundWithRoleRs {
  UUID: string,
  role: DataRoomFolderUserRole
}

export interface ActivityLogFilter {
  folderBoxIdList: string[];
  orgUUID: string;
  page: number;
  name: string;
  date: Date;
}

export interface DataRoomUserLogDTO {
  id: number;
  fullNameUser: string;
  emailUser: string;
  rootFolderId: string;
  rootFolderName: string;
  files: DataRoomUserLogDocumentDTO[];
}

export interface FilteredUserLogWrapper {
  logs: DataRoomUserLogDTO[];
  totalAmount: number;
}

export interface DataRoomUserLogDocumentDTO {
  id: number;
  documentName: string;
  documentId: string;
  parentFolderId: string;
  parentFolderName: string;
  action: DataRoomUserLogAction;
  createdTime: Date;
  boxEntityType: BoxEntityType;
}

export interface InputDataRoomFolder {
  idFolder: string;
  folderName: string;
  orgSelected: Organization;
}

export interface DataRoomUnpublishFromFund {
  orgUUID: string;
  fundUUID: string;
  folderId: string;
}

export interface DataRoomPublishToFund extends DataRoomUnpublishFromFund {
  role: DataRoomFolderUserRole;
}

export interface DataRoomFolderUserRoleChanger {
  email: string;
  folderBoxId: string;
  role: DataRoomFolderUserRole;
}

export interface DataRoomInvitation extends DataRoomFolderUserRoleChanger{
  name: string;
  sendNotification: boolean;
}

export interface DataRoomNotificationChanger {
  folderBoxId: string;
  // Only changed notifications
  notifications: DataRoomEmployeeNotificationDTO[];
}

export interface DataRoomRenameExtUser {
  newName: string;
  email: string;
  orgUUID: string;
}

export interface ExternalUserDenyAccess {
  email: string;
  folderBoxId: string;
}

export interface ExternalUserFolderMembershipDTO {
  action: DRExternalUserState;
  userName: string;
  userEmail: string;
  role: DataRoomFolderUserRole;
  folderBoxId: string;
  time: Date;
}

export interface DataRoomFolderCreator {
  orgUUID: string;
  folderName: string;
}

export interface DataRoomFolderNameEditor {
  orgUUID: string;
  folderId: string;
  newFolderName: string;
}

export interface DataRoomFolderNameEraser {
  orgUUID: string;
  folderId: string;
}

export interface DisplayLog {
  fullNameUser: string;
  emailUser: string;
  rootFolderId: string;
  rootFolderName: string;
  documentName: string;
  documentId: string;
  parentFolderId: string;
  parentFolderName: string;
  action: DataRoomUserLogAction;
  createdTime: Date;
  actionText: string;
  boxEntityType: BoxEntityType;
}

export interface BoxDataRoomExternalInfo {
  accessToken: string;
  folderId: string;
  firmName: string;
  firmLogo: string;
  email: string;
  fullName: string;
  adminLogin: boolean;
  role: DataRoomFolderUserRole;
}

export class DataRoomStates {
  showSwitchers = false;
  showEditNameMode = false;
  showModalCopyFolder = false;
  showModalCreationFolder = false;
  showModalInviteUser = false;
  showInvitationSent = false;
  showModalRenameExtUser = false;
  showMatchingEmails = false;
  showEmptyActivityLogState = false;
  showModalThinkTwice = false;
  dataRoomInitPage = true;
}

export class DataRoomExternalStates {
  showModalLogout = false;
  showModalAuthorize = false;
}

export class DataRoomExternalUserWrapper {
  states = new DataRoomExternalStates();
  email: string = '';
  selectedOrg: DRExternalUserAccessesInOrg = null;
  availableOrgs: DRExternalUserAccessesInOrg[];
  selectedFolder: DRExternalUserAccessFolder = null;
  availableFolders: DRExternalUserAccessFolder[];
}

export const spinnerProps: SpinnerProps = {
  isSpinnerActive: false,
  notActiveOnApply: true,
};

export class DataRoomExternalUserForms {
  foundExtUsers = new Map<string, string>();
  formInviteUser: FormGroup<{
    link: FormControl<string>,
    name: FormControl<string>,
    email: FormControl<string>,
    role: FormControl<DataRoomFolderUserRole>,
    sendNotification: FormControl<boolean>,
  }>;

  userIsAlreadyInvited: boolean = false;

  formInviteUserButtons: ModalButtons = {
    apply: 'Send',
    dismiss: 'Cancel',
  };

  formRenameExtUser: FormGroup<{
    newName: FormControl<string>,
    email: FormControl<string>,
  }>;

  formRenameExtUserButtons: ModalButtons = {
    apply: 'Rename',
    dismiss: 'Cancel',
  };

  constructor(private formBuilder: FormBuilder) {
    this.formInviteUser = this.formBuilder.group({
      link: new FormControl({ disabled: true, value: window.location.origin + shareLink }),
      name: new FormControl<string>('', { validators: [ Validators.required ] }),
      email: new FormControl<string>('', { validators: [ Validators.required, Validators.email ] }),
      role: new FormControl<DataRoomFolderUserRole>(undefined, { validators: [ Validators.required ] }),
      sendNotification: new FormControl<boolean>(true),
    });
    this.formRenameExtUser = this.formBuilder.group({
      newName: new FormControl<string>('', { validators: [ Validators.required ] }),
      email: new FormControl({ value: '', disabled: true }),
    });
  }
}

export class DataRoomFolderForms {
  formFolderName: FormGroup<{
    name: FormControl<string>,
  }>;

  formCreateFolder: FormGroup<{
    name: FormControl<string>,
  }>;

  formCopyFolder: FormGroup<{
    folderNameToCopy: FormControl<string>,
  }>;

  formCreateFolderButtons: ModalButtons = {
    apply: 'Apply',
    dismiss: 'Cancel',
  };

  constructor(private formBuilder: FormBuilder) {
    this.formFolderName = this.formBuilder.group({
      name: new FormControl<string>({ value: '', disabled: true }, { validators: [ Validators.required ] }),
    });
    this.formCreateFolder = this.formBuilder.group({
      name: new FormControl<string>('', { validators: [ Validators.required ] }),
    });
    this.formCopyFolder = this.formBuilder.group({
      folderNameToCopy: new FormControl<string>('', { validators: [ Validators.required ] }),
    });
  }
}

export class DataRoomLogForms {
  formSearchGroup: FormGroup<{
    filterName: FormControl<string>,
    filterDate: FormControl<Date>,
  }>;

  displayLogs: DisplayLog[] = [];

  constructor(private formBuilder: FormBuilder) {
    this.formSearchGroup = this.formBuilder.group({
      filterName: new FormControl<string>(''),
      filterDate: new FormControl<Date>(undefined),
    });
  }
}

export class DRCopyFolderRq {
  folderNameToCopy: string;
  fromFolderID: string;
}
